import React from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  Box,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Search as AnalyzeIcon,
  AutoAwesome as SegmentIcon,
  CheckCircle as SelectIcon,
  ContentCut as CropIcon,
  Done as CompleteIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const steps = [
  { label: 'Upload', icon: UploadIcon, stage: 'uploaded' },
  { label: 'Analyze', icon: AnalyzeIcon, stage: 'analyzing' },
  { label: 'Segment', icon: SegmentIcon, stage: 'segmenting' },
  { label: 'Select', icon: SelectIcon, stage: 'selecting' },
  { label: 'Crop', icon: CropIcon, stage: 'cropping' },
  { label: 'Complete', icon: CompleteIcon, stage: 'complete' }
];

const ProcessingSteps = ({ currentStage, progress, message }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const activeStep = steps.findIndex(step => step.stage === currentStage);
  
  const StepIcon = ({ icon: Icon, active, completed, isLastStep }) => {
    // For the last step (Complete), treat active as completed
    const isComplete = completed || (isLastStep && active);
    
    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: completed && !isLastStep
            ? 'transparent' // Transparent background for completed
            : (active || (isLastStep && isComplete))
              ? 'primary.main' // Colored background for active/complete
              : 'rgba(148, 163, 184, 0.1)', // Light background for pending
          border: '2px solid',
          borderColor: completed && !isLastStep
            ? 'success.main' // Green border for completed
            : (active || (isLastStep && isComplete))
              ? 'primary.main' // Primary border for active
              : 'transparent', // No border for pending
          transition: 'all 0.3s ease'
        }}
      >
        <Icon 
          sx={{ 
            fontSize: 20,
            color: completed && !isLastStep
              ? 'success.main' // Green icon for completed
              : (active || (isLastStep && isComplete))
                ? 'white' // White icon for active/complete
                : 'text.secondary' // Gray for pending
          }} 
        />
      </Box>
    );
  };
  
  // Check if we should show the spinner
  const showSpinner = activeStep >= 0 && activeStep < steps.length - 1;
  
  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Paper
        sx={{
          p: 3,
          background: 'rgba(30, 41, 59, 0.6)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Stepper 
          activeStep={activeStep} 
          orientation={isMobile ? 'vertical' : 'horizontal'}
          connector={null}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            '& .MuiStep-root': {
              px: 2,
              flex: isMobile ? 'initial' : '1',
              maxWidth: isMobile ? 'initial' : '150px'
            }
          }}
        >
          {steps.map((step, index) => (
            <Step key={step.label} completed={index < activeStep}>
              <StepLabel
                StepIconComponent={() => (
                  <StepIcon 
                    icon={step.icon} 
                    active={index === activeStep}
                    completed={index < activeStep}
                    isLastStep={index === steps.length - 1}
                  />
                )}
              >
                <Typography 
                  variant="body2" 
                  sx={{
                    color: index === activeStep 
                      ? 'text.primary' 
                      : 'text.secondary',
                    fontWeight: index === activeStep ? 600 : 400
                  }}
                >
                  {step.label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <AnimatePresence mode="wait">
          {message && (
            <motion.div
              key={message}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ 
                mt: 3, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: 2
              }}>
                {showSpinner && (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: 'primary.light'
                    }}
                  />
                )}
                <Typography variant="body1" color="primary.light">
                  {message}
                </Typography>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Paper>
    </Box>
  );
};

export default ProcessingSteps;